// Catalog.ID Client — `bitcash-auth-token` envelope types (§4.10)
// ============================================================================
// The purpose a module asks with when it wants a SPENDING TOKEN rather than a
// key: "let me debit this member's wallet, within these bounds, to pay this
// payee". The reply carries a 34-character AUTH token wrapped to the requester's
// ephemeral key — never the wallet key, which is bag-only and never leaves the
// bag (D5).
//
// IT RIDES THE EXISTING `key-grant-request` ENVELOPE, deliberately. §4.10 writes
// the shape with a bare `ephemeralPublicKey` and no `mode`/`keyId`; the shipped
// envelope carries `ephemeralPubkey`, `mode` and `keyId`, and — this is the
// load-bearing part — the module-identity signature is computed over exactly
// those fields (`verify.ts` / `KeyGrantClient.BuildSignedPayload`). A request
// that dropped them would be a request whose wrap target and key id are not
// committed by any signature. So a bitcash request is:
//
//     mode: 'release', keyId: 'bitcash_wallet', purpose: 'bitcash-auth-token',
//     ephemeralPubkey: <the requester's P-256 ephemeral>, scope: {…}
//
// and `scope`, `walletLabel` and `previousTokenRejected` ride outside the
// signature — which is why NOTHING in them is taken as authority. They are
// SHOWN to the member in the consent dialog, and the member's decision is the
// authority. That is the same posture the other families take toward everything
// they render.

import type { KeyGrantRequest } from './types';
import type { WrappedKey } from './crypto';
import type { GrantPolicyEntry } from './grant-policy';
import type { DefaultTokenCache } from './bitcash-token-cache';
import type { BitcashWorkerApi } from './bitcash-worker-client';
import type { WalletSlot } from '../crypto/bitcash-wallets-doc';
import type { DefaultTokenTerms } from '../auth/payment-flow';

/** The purpose string. Pinned; consumers send exactly this (§8). */
export const BITCASH_AUTH_TOKEN_PURPOSE = 'bitcash-auth-token';

/** What the module says it wants the token to be able to do (§4.10). */
export interface BitcashTokenScope {
    /**
     * REQUIRED (D6). `"operator"` — the shared default token: unbound, it pays
     * Catalog services at any operator, with Catalog.ID's service types and caps,
     * never the module's (apps/docs/plans/second-node-and-bitcash-open-issues.md
     * §9.3) — or a FUND receive address the module's own token is bound to. A
     * request without it is refused: no module obtains an unbound token with its
     * own scope.
     */
    payee: string;
    /** Domains the token may be used at. Empty ⇒ no site restriction. */
    sites?: string[];
    /** Service-type categories it may pay for. Empty ⇒ no restriction. */
    serviceTypes?: string[];
    /** Bits per CET day. Required (positive) for a non-operator payee. */
    dailyCap?: number;
    /** Bits per CET month. Required (positive) for a non-operator payee. */
    monthlyCap?: number;
    /** Unix ms, or null for a non-expiring token. */
    expiresAt?: number | null;
}

export interface BitcashAuthTokenRequest extends KeyGrantRequest {
    purpose: typeof BITCASH_AUTH_TOKEN_PURPOSE;
    /** RFC 3339 UTC. Informational — the envelope's own ordering is the hub's. */
    requestedAt?: string;
    scope?: BitcashTokenScope;
    /** Which of the member's wallets the module would prefer, by label. A
     *  preference for the picker, never a selection. */
    walletLabel?: string;
    /**
     * Retry marker (§4.12, cache invalidation): the module is re-asking because
     * the token it last got was refused with 403 `revoked` / `expired`. The
     * device cache is dropped before this request is served, so the answer is
     * refilled from the Worker or freshly minted. Consumers: set it ONLY after
     * such a refusal — it costs the member a supersecret prompt.
     */
    previousTokenRejected?: boolean;
}

/**
 * §4.10's response. `status` is this family's own vocabulary — `granted` rather
 * than the key families' `approved`, because what is released is an
 * authorisation, not a key. The three wrap fields are ALSO mirrored at the top
 * level so a consumer built on the shipped `KeyGrantClient` unwrap path reads
 * them where it already looks.
 */
export interface BitcashAuthTokenResponse {
    type: 'key-grant-response';
    to: string;
    requestId: string;
    status: 'granted' | 'denied' | 'error';
    reason?: string;
    /** The ECDH-wrapped 34-character display token. */
    wrapped?: WrappedKey;
    cidEphemeralPubkey?: string;
    nonce?: string;
    ciphertext?: string;
    /** Non-spendable handle of the wallet the token debits, when known. */
    walletRef?: string;
    /** `catalog:default` or `catalog:<requesterId>`. */
    label?: string;
    /** ISO-8601, or null for a non-expiring token. */
    expiresAt?: string | null;
}

/** One entry in the consent dialog's wallet picker. */
export interface BitcashWalletChoice {
    /** `account:<keyIndex>` or `org:<orgCid>`. Stable; the policy key is
     *  derived from it, so a grant never covers another wallet. */
    id: string;
    kind: 'account' | 'org';
    /** Account choices only: the bag slot (0 = the registration wallet). */
    keyIndex?: number;
    /** Organization choices only. */
    orgCid?: string;
    /** What the picker shows. */
    label: string;
    /**
     * True when the label is a placeholder because the account's wallet
     * document could not be read — a locked tab, an account created before
     * cid-encrypted-storage §7.2, or a registration whose wallet write failed.
     * Was the COMMON case before that: a wallet key was never device-stored
     * (D5), so a PIN-unlocked session held no entry to read a label from.
     */
    provisional?: boolean;
}

/** The organization wallet offered to an owner or treasurer delegate (D14). */
export interface BitcashOrgWalletOffer {
    orgCid: string;
    /** The organization's name, for the picker row. */
    label: string;
}

export interface BitcashDispatchDeps {
    /** The account's ACTIVE wallets, from its `cid/bitcash-wallets` document
     *  (cid-encrypted-storage §7.2). Async — it is a blob read, not a bag
     *  filter. `[]` when the tab is locked or the account has no document. */
    listAccountWallets?: () => Promise<WalletSlot[]>;
    /** The organization wallet, when the active delegate is an owner or
     *  treasurer and the org's wrapped keys carry one. */
    getOrgWalletOffer?: () => Promise<BitcashOrgWalletOffer | null>;
    /** The consent dialog. Absent ⇒ the purpose refuses: a spending
     *  authorisation is never released without asking. */
    confirmBitcashAuthToken?: (input: BitcashConsentInput) => Promise<BitcashConsentResult>;
    /** The chosen ACCOUNT wallet's spending key, out of the wallet document. No
     *  prompt: personal wallets are PIN-level since D5 was relaxed
     *  (cid-encrypted-storage §7.1). Null ⇒ unavailable. */
    getAccountWalletKey?: (choice: BitcashWalletChoice) => Promise<string | null>;
    /** The ORGANIZATION wallet's spending key, through the org key wraps: PIN,
     *  never a supersecret (§4.13). Null ⇒ unavailable. */
    getOrgWalletKey?: (orgCid: string) => Promise<string | null>;
    worker?: BitcashWorkerApi;
    /** Absent ⇒ no caching; every operator request refills. */
    cache?: DefaultTokenCache | null;
    /** The default token's terms from `GET /api/registration/config` → `defaultToken`: D27's caps,
     *  whole BIT, and the service types (§9.3 D-a); null when the node names none (go-live
     *  configuration). Absent ⇒ no default token. */
    getDefaultTokenCaps?: () => Promise<DefaultTokenTerms | null>;
    wrapForRequester?: (ephemeralPubkeyB64: string, plaintext: Uint8Array) => Promise<WrappedKey>;
    checkPolicy?: (cid: string, requesterId: string, keyId: string) => 'always' | 'session' | 'none';
    grantAlways?: (cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>) => void;
    grantSession?: (cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>) => void;
    /** Clock seam for tests. */
    now?: () => number;
}

/** What the consent dialog is told. Everything here is display text. */
export interface BitcashConsentInput {
    /** Hub-attested requester id. */
    requesterId: string;
    /** The module's own name, when it gave one. */
    requesterLabel?: string;
    /** The account the wallet belongs to. */
    owningCid: string;
    /** The picker's rows, in order; the first is the default. */
    choices: BitcashWalletChoice[];
    /** The label the token will carry. */
    tokenLabel: string;
    /** True for the shared default token (payee `operator`), whose caps and
     *  service types are the operator's, not the requester's. */
    isDefaultToken: boolean;
    /** What the module asked for — rendered for information in both paths. */
    scope: {
        payee: string;
        sites: string[];
        serviceTypes: string[];
        dailyCap: number | null;
        monthlyCap: number | null;
        expiresAt: number | null;
    };
    /** The terms the default token will actually carry — caps and service types (default path only). */
    effectiveCaps?: DefaultTokenTerms;
}

export interface BitcashConsentResult {
    decision: 'deny' | 'once' | 'session' | 'always';
    /** The picked wallet's {@link BitcashWalletChoice.id}. Ignored on `deny`. */
    walletId?: string;
}
